/**
 * Scoreboard (/prioritization/scoreboard)
 *
 * Org-wide scoreboard. Four views:
 *   1. Overall ranking   — every item ranked across all completed sessions
 *   2. Results           — completed sessions, each with its final ranking
 *   3. Tournaments       — tournament rounds and winners; shown only while the
 *                          tournaments module is on
 *   4. Most often on top — how often each item ranked first, second or third
 *
 * Labels on this page stay plain: no league, hall-of-fame or medal wording.
 *
 * Live updates via Supabase Realtime on session_results, session_events,
 * and tournaments. Polling fallback every 30 s when Realtime unavailable.
 *
 * Source: DOCS_V4/00-canon/ — read-only view; no writes from this surface.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AgentBar } from '../../components/agents/AgentBar';
import { twinsAgent, inversionAgent } from '../../components/agents/contextualAgents';
import {
  Trophy,
  TrendingUp,
  TrendingDown,
  Minus,
  Layers,
  Clock,
  Users,
  BarChart2,
  Radio,
  Activity,
  RefreshCw,
  Medal,
  Star,
  Flame,
  ChevronRight,
  Zap,
  GitBranch,
  Crown,
  Target,
  CalendarDays,
} from 'lucide-react';
import {
  useScoreboardStore,
  selectLiveSessions,
  sessionStateBadgeClass,
  tournamentStateBadgeClass,
  eventTypeLabel,
} from '../../stores/prioritization/scoreboardStore';
import type {
  RankedItem,
  ActivityEvent,
  ScoreboardSession,
  CompletedSessionSummary,
  ScoreboardTournament,
  HallOfFameEntry,
} from '../../stores/prioritization/scoreboardStore';
import { PageHeader } from '../../components/prioritization/PageHeader';
import { useItemsStore } from '../../stores/prioritization/itemsStore';
import { useModuleGate } from '../../lib/moduleGate';
import { sessionStateLabel } from '@/lib/sessions/vocabulary';

// ─── Types ─────────────────────────────────────────────────────────────────────

type ScoreboardView = 'standings' | 'results' | 'tournaments' | 'hall-of-fame';

// ─── Helpers ──────────────────────────────────────────────────────────────────

function relativeTime(date: Date): string {
  const diffMs = Date.now() - date.getTime();
  const secs = Math.floor(diffMs / 1000);
  if (secs < 5) return 'just now';
  if (secs < 60) return `${secs}s ago`;
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `${days}d ago`;
  return date.toLocaleDateString();
}

function formatDate(date: Date): string {
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

function initials(name: string): string {
  return name
    .split(' ')
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? '')
    .join('');
}

// ─── Medal / Rank visuals ─────────────────────────────────────────────────────

function RankBadge({ rank, size = 'md' }: { rank: number; size?: 'sm' | 'md' | 'lg' }) {
  const dim = size === 'sm' ? 'w-6 h-6' : size === 'lg' ? 'w-10 h-10' : 'w-7 h-7';
  const iconSize = size === 'sm' ? 'h-3 w-3' : size === 'lg' ? 'h-5 w-5' : 'h-3.5 w-3.5';
  const textSize = size === 'sm' ? 'text-[10px]' : size === 'lg' ? 'text-base' : 'text-xs';

  if (rank === 1)
    return (
      <div className={`${dim} rounded-full bg-electric-mustard-100 border-2 border-electric-mustard-400 flex items-center justify-center flex-shrink-0`}>
        <Trophy className={`${iconSize} text-electric-mustard-700`} />
      </div>
    );
  if (rank === 2)
    return (
      <div className={`${dim} rounded-full bg-[var(--hover-tint)] border-2 border-[var(--rule)] flex items-center justify-center flex-shrink-0`}>
        <span className={`${textSize} font-bold font-mono text-[var(--text-ter)]`}>2</span>
      </div>
    );
  if (rank === 3)
    return (
      <div className={`${dim} rounded-full bg-[var(--mustard-dim)] border-2 border-[var(--mustard)] flex items-center justify-center flex-shrink-0`}>
        <span className={`${textSize} font-bold font-mono text-[var(--mustard)]`}>3</span>
      </div>
    );
  return (
    <div className={`${dim} rounded-full bg-[var(--surface)] border border-[var(--rule)] flex items-center justify-center flex-shrink-0`}>
      <span className={`${textSize} font-mono text-[var(--text-ter)]`}>{rank}</span>
    </div>
  );
}

// ─── Delta Indicator ──────────────────────────────────────────────────────────

function DeltaIndicator({ delta }: { delta: number | null }) {
  if (delta == null) return <div className="w-4" />;
  if (delta > 0)
    return (
      <div className="flex items-center gap-0.5 text-primary-600" title={`Up ${delta}`}>
        <TrendingUp className="h-3 w-3" />
        <span className="text-xs font-mono">{delta}</span>
      </div>
    );
  if (delta < 0)
    return (
      <div className="flex items-center gap-0.5 text-luminous-coral-600" title={`Down ${Math.abs(delta)}`}>
        <TrendingDown className="h-3 w-3" />
        <span className="text-xs font-mono">{Math.abs(delta)}</span>
      </div>
    );
  return <Minus className="h-3 w-3 text-[var(--text-ter)]" />;
}

// ─── Score Bar ────────────────────────────────────────────────────────────────

function ScoreBar({ score, prevScore, accent = false }: { score: number; prevScore?: number; accent?: boolean }) {
  const [displayed] = useState(score);
  const increased = prevScore != null && score > prevScore;
  const colorClass = accent
    ? 'bg-electric-mustard-400'
    : increased
    ? 'bg-primary-500'
    : 'bg-primary-400';

  return (
    <div className="flex items-center gap-2 flex-1 min-w-0">
      <div className="flex-1 bg-[var(--hover-tint)] rounded-full h-2 overflow-hidden">
        <div
          className={`h-full rounded-full transition-all duration-700 ease-out ${colorClass}`}
          style={{ width: `${Math.max(2, displayed)}%` }}
        />
      </div>
      <span className="text-xs font-mono text-[var(--text-ter)] w-8 text-right flex-shrink-0">
        {displayed}%
      </span>
    </div>
  );
}

// ─── Stat Cards ───────────────────────────────────────────────────────────────

function StatCard({
  label,
  value,
  sub,
  icon,
  accent = false,
}: {
  label: string;
  value: string | number;
  sub?: string;
  icon?: React.ReactNode;
  accent?: boolean;
}) {
  return (
    <div className={` border px-5 py-4 flex flex-col gap-1 ${accent ? 'bg-electric-mustard-50 border-electric-mustard-200' : 'bg-[var(--bg-raised)] border-[var(--rule)]'}`}>
      <div className="flex items-center gap-1.5">
        {icon && <span className="text-[var(--text-ter)]">{icon}</span>}
        <span className="text-xs font-mono uppercase tracking-widest text-[var(--text-ter)]">{label}</span>
      </div>
      <span className={`text-2xl font-bold font-heading leading-none ${accent ? 'text-electric-mustard-800' : 'text-[var(--text-pri)]'}`}>{value}</span>
      {sub && <span className="text-xs text-[var(--text-ter)]">{sub}</span>}
    </div>
  );
}

// ─── Tab Navigation ───────────────────────────────────────────────────────────

interface TabProps {
  active: boolean;
  onClick: () => void;
  icon: React.ReactNode;
  label: string;
  count?: number;
}

function Tab({ active, onClick, icon, label, count }: TabProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium  transition-colors ${
        active
          ? 'bg-primary-50 text-primary-700 border border-primary-200'
          : 'text-[var(--text-ter)] hover:text-[var(--text-sec)] hover:bg-[var(--surface)] border border-transparent'
      }`}
    >
      {icon}
      {label}
      {count != null && (
        <span className={`text-xs font-mono px-1.5 py-0.5 rounded-full ${active ? 'bg-primary-100 text-primary-600' : 'bg-[var(--hover-tint)] text-[var(--text-ter)]'}`}>
          {count}
        </span>
      )}
    </button>
  );
}

// ─── Live Session Card ────────────────────────────────────────────────────────

function LiveSessionCard({
  session,
  scoredCount,
}: {
  session: ScoreboardSession;
  scoredCount: number;
}) {
  const total = session.itemIds.length || 1;
  const pct = Math.min(100, Math.round((scoredCount / total) * 100));

  return (
    <div className="bg-[var(--bg-raised)] border border-[var(--rule)] px-4 py-3 flex items-center gap-3 min-w-0 flex-shrink-0 w-56 relative overflow-hidden">
      <span className="absolute top-0 left-0 right-0 h-0.5 bg-primary-400 animate-pulse" />
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium text-[var(--text-pri)] truncate">{session.name}</div>
        <div className="flex items-center gap-2 mt-1">
          <span
            className={`text-xs px-1.5 py-0.5 rounded font-mono ${sessionStateBadgeClass(session.state)}`}
          >
            {sessionStateLabel(session.state)}
          </span>
          <span className="text-xs text-[var(--text-ter)] flex items-center gap-1">
            <Users className="h-3 w-3" />
            {session.participantIds.length}
          </span>
        </div>
        {scoredCount > 0 && (
          <div className="mt-2">
            <div className="flex justify-between text-[10px] text-[var(--text-ter)] mb-0.5">
              <span>{scoredCount}/{total} items</span>
              <span>{pct}%</span>
            </div>
            <div className="bg-[var(--hover-tint)] rounded-full h-1">
              <div
                className="bg-primary-400 h-1 rounded-full transition-all duration-500"
                style={{ width: `${pct}%` }}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── STANDINGS VIEW ───────────────────────────────────────────────────────────

interface StandingsViewProps {
  rankedItems: RankedItem[];
  itemNameMap: Map<string, string>;
  sessionNameMap: Record<string, string>;
  prevScores: Map<string, number>;
  newItemIds: Set<string>;
  activityFeed: ActivityEvent[];
  isLoading: boolean;
}

function StandingsView({
  rankedItems,
  itemNameMap,
  sessionNameMap,
  prevScores,
  newItemIds,
  activityFeed,
  isLoading,
}: StandingsViewProps) {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      {/* Rankings panel (2/3) */}
      <div className="lg:col-span-2">
        <div className="bg-[var(--bg-raised)] border border-[var(--rule)] overflow-hidden">
          <div className="flex items-center justify-between px-4 py-3 border-b border-[var(--rule)] bg-[var(--surface)]/50">
            <div className="flex items-center gap-2">
              <Layers className="h-4 w-4 text-[var(--text-ter)]" />
              <span className="text-sm font-semibold text-[var(--text-sec)]">Overall ranking</span>
              {rankedItems.length > 0 && (
                <span className="text-xs font-mono text-[var(--text-ter)] bg-[var(--hover-tint)] px-2 py-0.5 rounded-full">
                  {rankedItems.length}
                </span>
              )}
            </div>
            <div className="hidden sm:flex items-center gap-4 text-xs text-[var(--text-ter)]">
              <span className="w-8 text-center">Δ</span>
              <span className="w-36 text-center">Score</span>
            </div>
          </div>

          {isLoading && rankedItems.length === 0 ? (
            <div className="flex items-center justify-center py-16">
              <RefreshCw className="h-5 w-5 text-[var(--text-ter)] animate-spin" />
            </div>
          ) : rankedItems.length === 0 ? (
            <StandingsEmptyState />
          ) : (
            <div>
              {rankedItems.map((item, idx) => (
                <StandingsRow
                  key={item.itemId}
                  item={item}
                  itemName={itemNameMap.get(item.itemId) ?? `Item ${item.itemId.slice(0, 6)}`}
                  prevScore={prevScores.get(item.itemId)}
                  isNew={newItemIds.has(item.itemId)}
                  separator={idx < rankedItems.length - 1}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Activity feed (1/3) */}
      <div className="lg:col-span-1">
        <ActivityFeedPanel activityFeed={activityFeed} sessionNameMap={sessionNameMap} />
      </div>
    </div>
  );
}

function StandingsEmptyState() {
  return (
    <div className="flex flex-col items-center justify-center py-20 text-center">
      <div className="w-12 h-12 bg-[var(--hover-tint)] flex items-center justify-center mb-4">
        <BarChart2 className="h-6 w-6 text-[var(--text-ter)]" />
      </div>
      <h3 className="text-sm font-semibold text-[var(--text-sec)] mb-1">No ranking yet</h3>
      <p className="text-xs text-[var(--text-ter)] max-w-xs leading-relaxed">
        The overall ranking fills in as sessions complete. Run a prioritization session to see results here.
      </p>
    </div>
  );
}

function StandingsRow({
  item,
  itemName,
  prevScore,
  isNew,
  separator,
}: {
  item: RankedItem;
  itemName: string;
  prevScore?: number;
  isNew: boolean;
  separator: boolean;
}) {
  return (
    <div
      className={`group flex items-center gap-3 px-4 py-3 transition-colors hover:bg-[var(--surface)] ${
        isNew ? 'animate-fadeIn bg-primary-50/40' : ''
      } ${separator ? 'border-b border-[var(--rule)]' : ''}`}
    >
      <RankBadge rank={item.rank} />

      <div className="w-8 flex items-center justify-center">
        <DeltaIndicator delta={item.rankDelta} />
      </div>

      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium text-[var(--text-pri)] truncate leading-tight">{itemName}</div>
        <div className="text-xs text-[var(--text-ter)] mt-0.5">
          {item.sessionCount} session{item.sessionCount !== 1 ? 's' : ''}
          {' · '}
          {relativeTime(item.lastUpdated)}
        </div>
      </div>

      <div className="w-36 hidden sm:flex">
        <ScoreBar score={item.crossScore} prevScore={prevScore} accent={item.rank === 1} />
      </div>
      {/* The score bar is drawn from sm up; on a phone the score reads as a number (audit #43). */}
      <span className="sm:hidden text-xs font-mono tabular-nums text-[var(--text-sec)]">{item.crossScore}%</span>
    </div>
  );
}

// ─── RESULTS VIEW ─────────────────────────────────────────────────────────────

interface ResultsViewProps {
  completedSessions: CompletedSessionSummary[];
  itemNameMap: Map<string, string>;
}

function ResultsView({ completedSessions, itemNameMap }: ResultsViewProps) {
  if (completedSessions.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-center">
        <div className="w-12 h-12 bg-[var(--hover-tint)] flex items-center justify-center mb-4">
          <CalendarDays className="h-6 w-6 text-[var(--text-ter)]" />
        </div>
        <h3 className="text-sm font-semibold text-[var(--text-sec)] mb-1">No completed sessions</h3>
        <p className="text-xs text-[var(--text-ter)] max-w-xs leading-relaxed">
          Session results appear here once a session reaches Published or Scored state.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {completedSessions.map((session) => (
        <SessionResultCard key={session.id} session={session} itemNameMap={itemNameMap} />
      ))}
    </div>
  );
}

function SessionResultCard({
  session,
  itemNameMap,
}: {
  session: CompletedSessionSummary;
  itemNameMap: Map<string, string>;
}) {
  return (
    <Link
      to={`/prioritization/sessions/${session.id}`}
      className="block bg-[var(--bg-raised)] border border-[var(--rule)] hover:border-[var(--rule)] hover:shadow-sm transition-all overflow-hidden group"
    >
      {/* Card header — like a scoreboard game header */}
      <div className="flex items-center justify-between px-5 py-3 border-b border-[var(--rule)] bg-[var(--surface)]/50">
        <div className="flex items-center gap-2.5">
          <div className="w-7 h-7 bg-primary-50 border border-primary-100 flex items-center justify-center flex-shrink-0">
            <Target className="h-3.5 w-3.5 text-primary-600" />
          </div>
          <div>
            <div className="text-sm font-semibold text-[var(--text-pri)] group-hover:text-primary-700 transition-colors">
              {session.name}
            </div>
            <div className="flex items-center gap-3 mt-0.5">
              <span className="text-xs font-mono text-[var(--green)] dark:text-[var(--text-ter)] bg-[var(--green-dim)] border border-[var(--green)] px-1.5 py-0.5 rounded">
                FINAL
              </span>
              <span className="text-xs text-[var(--text-ter)] flex items-center gap-1">
                <CalendarDays className="h-3 w-3" />
                {formatDate(session.completedAt)}
              </span>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-4 text-xs text-[var(--text-ter)]">
          <span className="flex items-center gap-1">
            <Users className="h-3 w-3" />
            {session.participantCount} {session.participantCount === 1 ? 'participant' : 'participants'}
          </span>
          <span className="flex items-center gap-1">
            <Layers className="h-3 w-3" />
            {session.itemCount} items
          </span>
          <ChevronRight className="h-4 w-4 text-[var(--text-ter)] group-hover:text-[var(--text-ter)] transition-colors" />
        </div>
      </div>

      {/* Top items — like box score positions */}
      <div className="px-5 py-4">
        <div className="flex items-center gap-2 mb-3">
          <span className="text-[10px] font-mono uppercase tracking-widest text-[var(--text-ter)]">Final Ranking</span>
        </div>
        <div className="space-y-2">
          {session.topItems.slice(0, 5).map((topItem) => {
            const name = itemNameMap.get(topItem.itemId) ?? `Item ${topItem.itemId.slice(0, 6)}`;
            return (
              <div key={topItem.itemId} className="flex items-center gap-3">
                <RankBadge rank={topItem.rank} size="sm" />
                <span className="text-sm text-[var(--text-sec)] truncate flex-1">{name}</span>
                {topItem.score != null && (
                  <span className="text-xs font-mono text-[var(--text-ter)] flex-shrink-0">
                    {topItem.score.toFixed(1)}
                  </span>
                )}
              </div>
            );
          })}
          {session.itemCount > 5 && (
            <div className="text-xs text-[var(--text-ter)] pl-8 mt-1">
              +{session.itemCount - 5} more
            </div>
          )}
        </div>
      </div>
    </Link>
  );
}

// ─── TOURNAMENTS VIEW ─────────────────────────────────────────────────────────

interface TournamentsViewProps {
  tournaments: ScoreboardTournament[];
  itemNameMap: Map<string, string>;
}

function TournamentsView({ tournaments, itemNameMap }: TournamentsViewProps) {
  const completed = tournaments.filter((t) => t.state === 'COMPLETE');
  const active = tournaments.filter((t) => !['COMPLETE', 'CANCELLED', 'DRAFT'].includes(t.state));
  const drafts = tournaments.filter((t) => t.state === 'DRAFT');

  if (tournaments.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-center">
        <div className="w-12 h-12 bg-[var(--hover-tint)] flex items-center justify-center mb-4">
          <GitBranch className="h-6 w-6 text-[var(--text-ter)]" />
        </div>
        <h3 className="text-sm font-semibold text-[var(--text-sec)] mb-1">No tournaments yet</h3>
        <p className="text-xs text-[var(--text-ter)] max-w-xs leading-relaxed">
          Tournaments appear here when created. Each tournament runs multiple rounds to produce a champion.
        </p>
        <Link
          to="/prioritization/tournaments/new"
          className="mt-4 text-xs text-primary-600 hover:text-primary-700 font-medium underline"
        >
          Create first tournament →
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Champions wall — completed tournaments */}
      {completed.length > 0 && (
        <section>
          <div className="flex items-center gap-2 mb-4">
            <Crown className="h-4 w-4 text-electric-mustard-600" />
            <h2 className="text-sm font-semibold text-[var(--text-sec)] uppercase tracking-wide">Champions</h2>
            <span className="text-xs font-mono text-[var(--text-ter)] bg-[var(--hover-tint)] px-1.5 py-0.5 rounded-full">{completed.length}</span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {completed.map((t) => (
              <TournamentChampionCard key={t.id} tournament={t} itemNameMap={itemNameMap} />
            ))}
          </div>
        </section>
      )}

      {/* Active tournaments */}
      {active.length > 0 && (
        <section>
          <div className="flex items-center gap-2 mb-4">
            <Flame className="h-4 w-4 text-primary-500" />
            <h2 className="text-sm font-semibold text-[var(--text-sec)] uppercase tracking-wide">In Progress</h2>
            <span className="text-xs font-mono text-[var(--text-ter)] bg-[var(--hover-tint)] px-1.5 py-0.5 rounded-full">{active.length}</span>
          </div>
          <div className="space-y-4">
            {active.map((t) => (
              <TournamentProgressCard key={t.id} tournament={t} itemNameMap={itemNameMap} />
            ))}
          </div>
        </section>
      )}

      {/* Draft tournaments */}
      {drafts.length > 0 && (
        <section>
          <div className="flex items-center gap-2 mb-4">
            <Clock className="h-4 w-4 text-[var(--text-ter)]" />
            <h2 className="text-sm font-semibold text-[var(--text-sec)] uppercase tracking-wide">Upcoming</h2>
            <span className="text-xs font-mono text-[var(--text-ter)] bg-[var(--hover-tint)] px-1.5 py-0.5 rounded-full">{drafts.length}</span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {drafts.map((t) => (
              <TournamentDraftCard key={t.id} tournament={t} />
            ))}
          </div>
        </section>
      )}
    </div>
  );
}

function TournamentChampionCard({
  tournament,
  itemNameMap,
}: {
  tournament: ScoreboardTournament;
  itemNameMap: Map<string, string>;
}) {
  const champion = tournament.finalRankedItemIds?.[0];
  const runnerUp = tournament.finalRankedItemIds?.[1];
  const third = tournament.finalRankedItemIds?.[2];
  const roundCount = tournament.rounds.length;

  return (
    <Link
      to={`/prioritization/tournaments/${tournament.id}`}
      className="block bg-[var(--bg-raised)] border border-electric-mustard-200 hover:border-electric-mustard-300 hover:shadow-sm transition-all overflow-hidden group"
    >
      {/* Champion banner */}
      <div className="bg-electric-mustard-50 border-b border-electric-mustard-100 px-5 py-3">
        <div className="flex items-center gap-2 mb-1">
          <Crown className="h-4 w-4 text-electric-mustard-600 flex-shrink-0" />
          <span className="text-xs font-mono text-electric-mustard-600 uppercase tracking-widest">Champion</span>
        </div>
        <div className="text-base font-semibold text-[var(--text-pri)] truncate group-hover:text-primary-700 transition-colors">
          {champion
            ? (itemNameMap.get(champion) ?? `Item ${champion.slice(0, 6)}`)
            : 'No champion yet'}
        </div>
      </div>

      {/* Tournament info */}
      <div className="px-5 py-3 space-y-2">
        <div className="text-sm font-medium text-[var(--text-sec)]">{tournament.name}</div>

        {/* Podium */}
        {(runnerUp || third) && (
          <div className="flex items-center gap-3 mt-1">
            {runnerUp && (
              <div className="flex items-center gap-1.5">
                <RankBadge rank={2} size="sm" />
                <span className="text-xs text-[var(--text-ter)] truncate max-w-[80px]">
                  {itemNameMap.get(runnerUp) ?? `Item ${runnerUp.slice(0, 6)}`}
                </span>
              </div>
            )}
            {third && (
              <div className="flex items-center gap-1.5">
                <RankBadge rank={3} size="sm" />
                <span className="text-xs text-[var(--text-ter)] truncate max-w-[80px]">
                  {itemNameMap.get(third) ?? `Item ${third.slice(0, 6)}`}
                </span>
              </div>
            )}
          </div>
        )}

        <div className="flex items-center justify-between text-xs text-[var(--text-ter)] pt-1 border-t border-[var(--rule)] mt-2">
          <span className="flex items-center gap-1">
            <GitBranch className="h-3 w-3" />
            {roundCount} round{roundCount !== 1 ? 's' : ''}
          </span>
          {tournament.completedAt && (
            <span>{formatDate(tournament.completedAt)}</span>
          )}
          <ChevronRight className="h-3.5 w-3.5 text-[var(--text-ter)] group-hover:text-[var(--text-ter)] transition-colors" />
        </div>
      </div>
    </Link>
  );
}

function TournamentProgressCard({
  tournament,
  itemNameMap,
}: {
  tournament: ScoreboardTournament;
  itemNameMap: Map<string, string>;
}) {
  const completedRounds = tournament.rounds.filter((r) => r.state === 'COMPLETE').length;
  const totalRounds = tournament.rounds.length;
  const currentRound = tournament.rounds.find((r) => r.state === 'OPEN' || r.state === 'SESSIONS_COMPLETE' || r.state === 'AWAITING_ADVANCE');
  const advancingItems = currentRound?.advancingItemIds ?? [];

  return (
    <Link
      to={`/prioritization/tournaments/${tournament.id}`}
      className="block bg-[var(--bg-raised)] border border-[var(--rule)] hover:border-primary-200 hover:shadow-sm transition-all overflow-hidden group"
    >
      <div className="px-5 py-4">
        <div className="flex items-start justify-between gap-3 mb-3">
          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold text-[var(--text-pri)] group-hover:text-primary-700 transition-colors">{tournament.name}</div>
            <div className="flex items-center gap-2 mt-1">
              <span className={`text-xs px-1.5 py-0.5 rounded font-mono border ${tournamentStateBadgeClass(tournament.state)}`}>
                {tournament.state.replace(/_/g, ' ')}
              </span>
              {currentRound && (
                <span className="text-xs text-[var(--text-ter)]">
                  Round {currentRound.roundNumber}{currentRound.label ? ` · ${currentRound.label}` : ''}
                </span>
              )}
            </div>
          </div>
          <ChevronRight className="h-4 w-4 text-[var(--text-ter)] group-hover:text-[var(--text-ter)] flex-shrink-0 mt-1 transition-colors" />
        </div>

        {/* Progress bar */}
        {totalRounds > 0 && (
          <div className="mb-3">
            <div className="flex justify-between text-[10px] text-[var(--text-ter)] mb-1">
              <span>{completedRounds}/{totalRounds} rounds complete</span>
            </div>
            <div className="bg-[var(--hover-tint)] rounded-full h-1.5">
              <div
                className="bg-primary-400 h-1.5 rounded-full transition-all"
                style={{ width: `${Math.round((completedRounds / totalRounds) * 100)}%` }}
              />
            </div>
          </div>
        )}

        {/* Round bracket summary */}
        {tournament.rounds.length > 0 && (
          <div className="flex items-center gap-1 overflow-x-auto">
            {tournament.rounds.map((round, idx) => (
              <div key={round.id} className="flex items-center gap-1 flex-shrink-0">
                {idx > 0 && <ChevronRight className="h-3 w-3 text-[var(--text-ter)] flex-shrink-0" />}
                <div
                  className={`px-2 py-1 rounded text-[10px] font-mono border flex-shrink-0 ${
                    round.state === 'COMPLETE'
                      ? 'bg-[var(--green-dim)] border-[var(--green)] text-[var(--green)] dark:text-[var(--text-ter)]'
                      : round.state === 'OPEN' || round.state === 'SESSIONS_COMPLETE'
                      ? 'bg-primary-50 border-primary-200 text-primary-700'
                      : 'bg-[var(--surface)] border-[var(--rule)] text-[var(--text-ter)]'
                  }`}
                >
                  {round.label ?? `R${round.roundNumber}`}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Advancing items for current round */}
        {advancingItems.length > 0 && (
          <div className="mt-3 pt-3 border-t border-[var(--rule)]">
            <div className="text-[10px] font-mono uppercase tracking-widest text-[var(--text-ter)] mb-2">
              Advancing from current round
            </div>
            <div className="flex flex-wrap gap-1.5">
              {advancingItems.slice(0, 4).map((itemId) => (
                <span
                  key={itemId}
                  className="text-xs bg-primary-50 text-primary-700 border border-primary-100 px-2 py-0.5 rounded-full truncate max-w-[120px]"
                >
                  {itemNameMap.get(itemId) ?? `Item ${itemId.slice(0, 6)}`}
                </span>
              ))}
              {advancingItems.length > 4 && (
                <span className="text-xs text-[var(--text-ter)]">+{advancingItems.length - 4}</span>
              )}
            </div>
          </div>
        )}
      </div>
    </Link>
  );
}

function TournamentDraftCard({ tournament }: { tournament: ScoreboardTournament }) {
  return (
    <Link
      to={`/prioritization/tournaments/${tournament.id}`}
      className="block bg-[var(--bg-raised)] border border-[var(--rule)] hover:border-[var(--rule)] transition-all overflow-hidden group"
    >
      <div className="px-5 py-4">
        <div className="flex items-center justify-between">
          <div className="flex-1 min-w-0">
            <div className="text-sm font-medium text-[var(--text-sec)] group-hover:text-[var(--text-pri)] truncate transition-colors">{tournament.name}</div>
            <div className="flex items-center gap-2 mt-1">
              <span className="text-xs px-1.5 py-0.5 rounded font-mono border text-[var(--text-ter)] bg-[var(--surface)] border-[var(--rule)]">
                Draft
              </span>
              <span className="text-xs text-[var(--text-ter)] flex items-center gap-1">
                <Layers className="h-3 w-3" />
                {tournament.itemIds.length} items
              </span>
            </div>
          </div>
          <ChevronRight className="h-4 w-4 text-[var(--text-ter)] group-hover:text-[var(--text-ter)] flex-shrink-0 transition-colors" />
        </div>
      </div>
    </Link>
  );
}

// ─── HALL OF FAME VIEW ────────────────────────────────────────────────────────

interface HallOfFameViewProps {
  hallOfFame: HallOfFameEntry[];
  itemNameMap: Map<string, string>;
}

function HallOfFameView({ hallOfFame, itemNameMap }: HallOfFameViewProps) {
  if (hallOfFame.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-center">
        <div className="w-12 h-12 bg-electric-mustard-50 border border-electric-mustard-200 flex items-center justify-center mb-4">
          <Star className="h-6 w-6 text-electric-mustard-500" />
        </div>
        <h3 className="text-sm font-semibold text-[var(--text-sec)] mb-1">Nothing here yet</h3>
        <p className="text-xs text-[var(--text-ter)] max-w-xs leading-relaxed">
          This fills in as sessions complete. It counts how often each item ranked first, second or third.
        </p>
      </div>
    );
  }

  const top3 = hallOfFame.slice(0, 3);
  const rest = hallOfFame.slice(3, 25);

  return (
    <div className="space-y-8">
      {/* Podium — top 3 */}
      {top3.length > 0 && (
        <section>
          <div className="flex items-center gap-2 mb-5">
            <Star className="h-4 w-4 text-electric-mustard-500" />
            <h2 className="text-sm font-semibold text-[var(--text-sec)] uppercase tracking-wide">Top three</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {top3.map((entry, idx) => (
              <HallOfFamePodiumCard
                key={entry.itemId}
                entry={entry}
                rank={idx + 1}
                itemName={itemNameMap.get(entry.itemId) ?? `Item ${entry.itemId.slice(0, 6)}`}
              />
            ))}
          </div>
        </section>
      )}

      {/* Full leaderboard */}
      {rest.length > 0 && (
        <section>
          <div className="flex items-center gap-2 mb-4">
            <Medal className="h-4 w-4 text-[var(--text-ter)]" />
            <h2 className="text-sm font-semibold text-[var(--text-sec)] uppercase tracking-wide">The rest</h2>
          </div>
          <div className="bg-[var(--bg-raised)] border border-[var(--rule)] overflow-hidden">
            <div className="grid grid-cols-[auto_1fr_auto_auto_auto_auto] gap-x-4 items-center px-4 py-2.5 border-b border-[var(--rule)] bg-[var(--surface)]/50">
              <span className="text-[10px] font-mono uppercase tracking-widest text-[var(--text-ter)] w-7 text-center">#</span>
              <span className="text-[10px] font-mono uppercase tracking-widest text-[var(--text-ter)]">Item</span>
              <span className="text-[10px] font-mono uppercase tracking-widest text-[var(--text-ter)] w-12 text-center">1st</span>
              <span className="text-[10px] font-mono uppercase tracking-widest text-[var(--text-ter)] w-12 text-center">2nd</span>
              <span className="text-[10px] font-mono uppercase tracking-widest text-[var(--text-ter)] w-12 text-center">3rd</span>
              <span className="text-[10px] font-mono uppercase tracking-widest text-[var(--text-ter)] w-16 text-center">Avg</span>
            </div>
            {rest.map((entry, idx) => (
              <HallOfFameRow
                key={entry.itemId}
                entry={entry}
                rank={idx + 4}
                itemName={itemNameMap.get(entry.itemId) ?? `Item ${entry.itemId.slice(0, 6)}`}
                separator={idx < rest.length - 1}
              />
            ))}
          </div>
        </section>
      )}
    </div>
  );
}

function HallOfFamePodiumCard({
  entry,
  rank,
  itemName,
}: {
  entry: HallOfFameEntry;
  rank: number;
  itemName: string;
}) {
  const isChampion = rank === 1;

  return (
    <div
      className={` border overflow-hidden ${
        isChampion
          ? 'border-electric-mustard-300 bg-electric-mustard-50'
          : rank === 2
          ? 'border-[var(--rule)] bg-[var(--surface)]'
          : 'border-[var(--mustard)] bg-[var(--mustard-dim)]/50'
      }`}
    >
      {/* Rank banner */}
      <div className={`px-5 py-3 border-b ${isChampion ? 'border-electric-mustard-200' : rank === 2 ? 'border-[var(--rule)]' : 'border-[var(--mustard)]'}`}>
        <div className="flex items-center gap-2">
          <RankBadge rank={rank} size="lg" />
          <div>
            <div className="text-xs font-mono uppercase tracking-widest text-[var(--text-ter)]">
              {rank === 1 ? 'On top most often' : rank === 2 ? 'Second most often' : 'Third most often'}
            </div>
          </div>
        </div>
      </div>

      {/* Item info */}
      <div className="px-5 py-4">
        <div className="text-base font-semibold text-[var(--text-pri)] mb-3 leading-tight">{itemName}</div>

        <div className="grid grid-cols-3 gap-2 text-center">
          <div className="bg-[var(--bg-raised)]/60 px-2 py-2 border border-white/80">
            <div className="text-lg font-bold text-electric-mustard-700 font-heading leading-none">{entry.goldCount}</div>
            <div className="text-[10px] text-[var(--text-ter)] mt-0.5">1st place</div>
          </div>
          <div className="bg-[var(--bg-raised)]/60 px-2 py-2 border border-white/80">
            <div className="text-lg font-bold text-[var(--text-sec)] font-heading leading-none">{entry.silverCount}</div>
            <div className="text-[10px] text-[var(--text-ter)] mt-0.5">2nd place</div>
          </div>
          <div className="bg-[var(--bg-raised)]/60 px-2 py-2 border border-white/80">
            <div className="text-lg font-bold text-[var(--mustard)] font-heading leading-none">{entry.bronzeCount}</div>
            <div className="text-[10px] text-[var(--text-ter)] mt-0.5">3rd place</div>
          </div>
        </div>

        <div className="flex items-center justify-between mt-3 text-xs text-[var(--text-ter)]">
          <span>{entry.totalSessions} sessions</span>
          <span>avg rank {entry.avgRank.toFixed(1)}</span>
        </div>
      </div>
    </div>
  );
}

function HallOfFameRow({
  entry,
  rank,
  itemName,
  separator,
}: {
  entry: HallOfFameEntry;
  rank: number;
  itemName: string;
  separator: boolean;
}) {
  return (
    <div className={`grid grid-cols-[auto_1fr_auto_auto_auto_auto] gap-x-4 items-center px-4 py-3 hover:bg-[var(--surface)] transition-colors ${separator ? 'border-b border-[var(--rule)]' : ''}`}>
      <div className="w-7 flex justify-center">
        <span className="text-xs font-mono text-[var(--text-ter)]">{rank}</span>
      </div>
      <div className="min-w-0">
        <div className="text-sm font-medium text-[var(--text-pri)] truncate">{itemName}</div>
        <div className="text-xs text-[var(--text-ter)]">{entry.totalSessions} sessions</div>
      </div>
      <div className="w-12 text-center">
        <span className="text-sm font-semibold text-electric-mustard-700">{entry.goldCount}</span>
      </div>
      <div className="w-12 text-center">
        <span className="text-sm font-semibold text-[var(--text-ter)]">{entry.silverCount}</span>
      </div>
      <div className="w-12 text-center">
        <span className="text-sm font-semibold text-[var(--mustard)]">{entry.bronzeCount}</span>
      </div>
      <div className="w-16 text-center">
        <span className="text-sm font-mono text-[var(--text-ter)]">{entry.avgRank.toFixed(1)}</span>
      </div>
    </div>
  );
}

// ─── Activity Feed Panel ──────────────────────────────────────────────────────

function ActivityFeedPanel({
  activityFeed,
  sessionNameMap,
}: {
  activityFeed: ActivityEvent[];
  sessionNameMap: Record<string, string>;
}) {
  return (
    <div className="bg-[var(--bg-raised)] border border-[var(--rule)] overflow-hidden h-full">
      <div className="flex items-center gap-2 px-4 py-3 border-b border-[var(--rule)] bg-[var(--surface)]/50">
        <Clock className="h-4 w-4 text-[var(--text-ter)]" />
        <span className="text-sm font-semibold text-[var(--text-sec)]">Activity</span>
        {activityFeed.length > 0 && (
          <span className="text-xs font-mono text-[var(--text-ter)] bg-[var(--hover-tint)] px-2 py-0.5 rounded-full ml-auto">
            {activityFeed.length}
          </span>
        )}
      </div>

      {activityFeed.length === 0 ? (
        <div className="flex flex-col items-center py-10 text-center px-4">
          <Radio className="h-6 w-6 text-[var(--text-ter)] mb-2" />
          <p className="text-xs text-[var(--text-ter)] leading-relaxed">
            Session events will appear here as participants score and advance sessions.
          </p>
        </div>
      ) : (
        <div className="px-4 py-2 overflow-y-auto max-h-[520px]">
          {activityFeed.map((event) => (
            <ActivityEventRow
              key={event.id}
              event={event}
              sessionName={sessionNameMap[event.sessionId] ?? ''}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function ActivityEventRow({ event, sessionName }: { event: ActivityEvent; sessionName: string }) {
  const initStr = initials(event.actorName);
  return (
    <div className="flex items-start gap-2.5 py-2.5 border-b border-[var(--rule)] last:border-0 animate-slide-up">
      <div className="w-7 h-7 rounded-full bg-primary-100 text-primary-700 text-xs font-semibold flex items-center justify-center flex-shrink-0 mt-0.5">
        {initStr || '?'}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-xs text-[var(--text-sec)] leading-snug">
          <span className="font-medium">{event.actorName}</span>{' '}
          <span className="text-[var(--text-ter)]">{eventTypeLabel(event.eventType)}</span>
          {sessionName && (
            <>
              {' '}
              <span className="text-[var(--text-ter)]">in</span>{' '}
              <span className="text-[var(--text-sec)] italic truncate">{sessionName}</span>
            </>
          )}
        </p>
        <p className="text-[10px] text-[var(--text-ter)] mt-0.5 font-mono">{relativeTime(event.createdAt)}</p>
      </div>
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function Scoreboard() {
  const navigate = useNavigate();
  const pageAgents = useMemo(
    () => [twinsAgent(navigate), inversionAgent(navigate)],
    [navigate],
  );
  const {
    rankedItems,
    sessions,
    activityFeed,
    completedSessions,
    scoreboardTournaments,
    hallOfFame,
    activeCollectingSessionIds,
    isLoading,
    isRealtimeConnected,
    lastFetchedAt,
    initialize,
    teardown,
    refresh,
  } = useScoreboardStore();

  const items = useItemsStore((s) => s.items);
  const fetchItems = useItemsStore((s) => s.fetchFromSupabase);

  // Tournaments is a module a workspace can turn off; while it is off nothing
  // about tournaments shows here (tab, count card, subtitle).
  const tournamentsEnabled = useModuleGate('tournaments');
  const [selectedView, setView] = useState<ScoreboardView>('standings');
  const view: ScoreboardView =
    selectedView === 'tournaments' && !tournamentsEnabled ? 'standings' : selectedView;
  const [, setTick] = useState(0);

  // Relative-time ticker
  useEffect(() => {
    const t = setInterval(() => setTick((n) => n + 1), 30_000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    if (items.length === 0 && typeof fetchItems === 'function') {
      fetchItems();
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    initialize();
    return () => teardown();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Item name lookup
  const itemNameMap = useMemo(() => {
    const m = new Map<string, string>();
    for (const item of items) {
      if ('title' in item) m.set(item.id, (item as { id: string; title: string }).title);
    }
    return m;
  }, [items]);

  // Session name lookup
  const sessionNameMap = useMemo(() => {
    const m: Record<string, string> = {};
    for (const [id, s] of sessions.entries()) m[id] = s.name;
    return m;
  }, [sessions]);

  // Scored-item counts per session
  const { results } = useScoreboardStore.getState();
  const scoredCountBySession = useMemo(() => {
    const m = new Map<string, number>();
    for (const r of results) {
      m.set(r.sessionId, (m.get(r.sessionId) ?? 0) + 1);
    }
    return m;
  }, [results]);

  // Track previous scores for delta animation
  const prevScoresRef = useRef<Map<string, number>>(new Map());
  const prevScores = new Map(prevScoresRef.current);
  useEffect(() => {
    prevScoresRef.current = new Map(rankedItems.map((r) => [r.itemId, r.crossScore]));
  }, [rankedItems]);

  const liveSessions = useMemo(
    () => selectLiveSessions(sessions, activeCollectingSessionIds),
    [sessions, activeCollectingSessionIds],
  );
  const totalSessions = completedSessions.length;
  const totalTournaments = scoreboardTournaments.filter((t) => t.state === 'COMPLETE').length;
  const totalRanked = rankedItems.length;
  const championsCount = scoreboardTournaments.filter((t) => t.state === 'COMPLETE' && (t.finalRankedItemIds?.length ?? 0) > 0).length;

  // Track newly-appeared items (first time we see an itemId in ranked list)
  const seenItemIds = useRef<Set<string>>(new Set());
  const newItemIds = new Set<string>();
  for (const r of rankedItems) {
    if (!seenItemIds.current.has(r.itemId)) {
      newItemIds.add(r.itemId);
      seenItemIds.current.add(r.itemId);
    }
  }

  return (
    <div className="pt-3 pb-12 px-6">
      {/* ── Page Header ────────────────────────────────────────────────────── */}
      <PageHeader
        icon={<Trophy className="h-4 w-4 text-electric-mustard-600" />}
        iconBg="bg-electric-mustard-50"
        title="Scoreboard"
        subtitle={
          tournamentsEnabled
            ? 'Organization-wide results — sessions, tournaments, and the overall ranking'
            : 'Organization-wide results — sessions and the overall ranking'
        }
        actions={
          <div className="flex items-center gap-3">
            {isRealtimeConnected ? (
              <div className="flex items-center gap-1.5">
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary-400 opacity-75" />
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-primary-500" />
                </span>
                <span className="text-xs font-mono text-primary-600 uppercase tracking-widest">Live</span>
              </div>
            ) : (
              <div className="flex items-center gap-1.5">
                <Radio className="h-3 w-3 text-[var(--text-ter)]" />
                <span className="text-xs text-[var(--text-ter)]">Polling</span>
              </div>
            )}

            <button
              type="button"
              onClick={() => refresh()}
              className="flex items-center gap-1.5 text-xs text-[var(--text-ter)] hover:text-[var(--text-sec)] bg-[var(--hover-tint)] hover:bg-[var(--hover-tint)] border border-[var(--rule)] px-2.5 py-1.5 transition-colors"
            >
              <RefreshCw className={`h-3 w-3 ${isLoading ? 'animate-spin' : ''}`} />
              Refresh
            </button>

            {lastFetchedAt && (
              <span className="text-xs text-[var(--text-ter)] hidden md:block">
                Updated {relativeTime(lastFetchedAt)}
              </span>
            )}
          </div>
        }
      />

      {/* ── Agent bar — agents relevant to a ranking surface ───────────────── */}
      <AgentBar agents={pageAgents} variant="inline" className="mb-6" />

      {/* ── Org Stats Banner ───────────────────────────────────────────────── */}
      <div className={`grid grid-cols-2 gap-3 mb-6 ${tournamentsEnabled ? 'sm:grid-cols-4' : 'sm:grid-cols-3'}`}>
        <StatCard
          label="Sessions"
          value={totalSessions}
          sub="completed"
          icon={<Target className="h-3.5 w-3.5" />}
        />
        <StatCard
          label="Items Ranked"
          value={totalRanked}
          sub="cross-session"
          icon={<BarChart2 className="h-3.5 w-3.5" />}
        />
        <StatCard
          label="Live Now"
          value={liveSessions.length}
          sub={liveSessions.length > 0 ? 'scoring in progress' : 'no active sessions'}
          icon={<Zap className="h-3.5 w-3.5" />}
          accent={liveSessions.length > 0}
        />
        {tournamentsEnabled && (
          <StatCard
            label="Champions"
            value={championsCount > 0 ? championsCount : totalTournaments}
            sub={championsCount > 0 ? 'tournaments crowned' : 'tournaments total'}
            icon={<Crown className="h-3.5 w-3.5" />}
          />
        )}
      </div>

      {/* ── Live sessions strip ────────────────────────────────────────────── */}
      {liveSessions.length > 0 && (
        <div className="mb-6">
          <div className="flex items-center gap-2 mb-2">
            <Activity className="h-3.5 w-3.5 text-primary-500" />
            <span className="text-xs font-mono uppercase tracking-widest text-[var(--text-ter)]">
              In Progress Now
            </span>
          </div>
          <div className="flex gap-3 overflow-x-auto pb-1 -mx-1 px-1">
            {liveSessions.map((s) => (
              <LiveSessionCard
                key={s.id}
                session={s}
                scoredCount={scoredCountBySession.get(s.id) ?? 0}
              />
            ))}
          </div>
        </div>
      )}

      {/* ── Tab Navigation ────────────────────────────────────────────────── */}
      <div className="flex items-center gap-1.5 mb-6 overflow-x-auto pb-1">
        <Tab
          active={view === 'standings'}
          onClick={() => setView('standings')}
          icon={<Layers className="h-3.5 w-3.5" />}
          label="Overall ranking"
          count={totalRanked > 0 ? totalRanked : undefined}
        />
        <Tab
          active={view === 'results'}
          onClick={() => setView('results')}
          icon={<CalendarDays className="h-3.5 w-3.5" />}
          label="Results"
          count={completedSessions.length > 0 ? completedSessions.length : undefined}
        />
        {tournamentsEnabled && (
          <Tab
            active={view === 'tournaments'}
            onClick={() => setView('tournaments')}
            icon={<GitBranch className="h-3.5 w-3.5" />}
            label="Tournaments"
            count={scoreboardTournaments.length > 0 ? scoreboardTournaments.length : undefined}
          />
        )}
        <Tab
          active={view === 'hall-of-fame'}
          onClick={() => setView('hall-of-fame')}
          icon={<Star className="h-3.5 w-3.5" />}
          label="Most often on top"
          count={hallOfFame.length > 0 ? Math.min(hallOfFame.length, 25) : undefined}
        />
      </div>

      {/* ── View Content ──────────────────────────────────────────────────── */}
      {view === 'standings' && (
        <StandingsView
          rankedItems={rankedItems}
          itemNameMap={itemNameMap}
          sessionNameMap={sessionNameMap}
          prevScores={prevScores}
          newItemIds={newItemIds}
          activityFeed={activityFeed}
          isLoading={isLoading}
        />
      )}

      {view === 'results' && (
        <ResultsView
          completedSessions={completedSessions}
          itemNameMap={itemNameMap}
        />
      )}

      {view === 'tournaments' && (
        <TournamentsView
          tournaments={scoreboardTournaments}
          itemNameMap={itemNameMap}
        />
      )}

      {view === 'hall-of-fame' && (
        <HallOfFameView
          hallOfFame={hallOfFame}
          itemNameMap={itemNameMap}
        />
      )}
    </div>
  );
}
